/**
 * useBlockEditExitDialog — Track 3 Phase 1
 *
 * Owns the BCLOSE / BlockEditExit modal feature extracted from `App.tsx`
 * as the proof-of-pattern for the broader App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Mirrors `draftSession.step === "bclose-confirm"` into a local open
 *     flag (auto-open driven by the draft state machine).
 *   - Resolves the active block's display name (from
 *     `draftSession.options.blockName` or the active geometry space).
 *   - Owns the three confirmation handlers (Save / Discard / Cancel),
 *     each wrapped in `useCallback` so the props bundle keeps stable
 *     identities across renders that don't actually mutate the dialog
 *     state.
 *
 * Returned shape:
 *   { isOpen, props: { blockName, onSave, onDiscard, onCancel } }
 *
 * The orchestrator spreads `props` onto `<BlockEditExitDialog>`.
 *
 * AutoCAD parity:
 *   The native AutoCAD dialog reads "Сохранить изменения в блоке X?" and
 *   Save / Discard / Cancel correspond to BSAVE+exit / exit-with-rollback /
 *   stay-in-BEDIT respectively. The hook preserves that semantic via the
 *   `space/exit-block-edit` command (action: "save" | "discard").
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import type { DraftSession } from "../scene/draftingHelpers";
import { useEditorStore } from "../state/editorStore";
import { useT } from "../i18n";

export interface UseBlockEditExitDialogParams {
  /** Current draft session — auto-opens dialog when step === "bclose-confirm". */
  draftSession: DraftSession | null;
  /** Cleared by all three handlers so the CLI fallback prompt also closes. */
  setDraftSession: (session: DraftSession | null) => void;
  /** Cleared by all three handlers (matches App-scope cleanup pattern). */
  setCommandText: (text: string) => void;
  /** Receives the localized "block saved" / "block discarded" feed entries. */
  appendCommandFeed: (text: string) => void;
}

export interface UseBlockEditExitDialogResult {
  /** Whether to mount `<BlockEditExitDialog>`. */
  isOpen: boolean;
  /** Props bundle for `<BlockEditExitDialog>`. */
  props: {
    blockName: string;
    onSave: () => void;
    onDiscard: () => void;
    onCancel: () => void;
  };
}

export function useBlockEditExitDialog({
  draftSession,
  setDraftSession,
  setCommandText,
  appendCommandFeed,
}: UseBlockEditExitDialogParams): UseBlockEditExitDialogResult {
  const t = useT();
  const dispatch = useEditorStore((store) => store.dispatch);
  const geometryDocument = useEditorStore((store) => store.geometryDocument);

  // Local open flag mirrors the draft state machine. Kept as `useState`
  // (rather than a derived expression) so future Phase 2 work can layer
  // a "modal cancel" path on top without re-plumbing the parent.
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(draftSession?.step === "bclose-confirm");
    if (!draftSession) {
      setIsOpen(false);
    }
  }, [draftSession]);

  // Resolve the block name shown in the dialog header. Prefers the
  // value carried by the draft session's options bag (the BCLOSE
  // initiator stamps it in), falls back to the active geometry space's
  // `blockEditName` so a stale draft session still produces a usable
  // label.
  const blockName = useMemo(() => {
    const fromSession = draftSession?.options?.blockName;
    if (typeof fromSession === "string" && fromSession.length > 0) {
      return fromSession;
    }
    const activeSpace = geometryDocument.spaces.find(
      (s) => s.id === geometryDocument.activeSpaceId,
    );
    return activeSpace?.blockEditName ?? "";
  }, [draftSession, geometryDocument]);

  const closeAndClearDraft = useCallback(() => {
    setIsOpen(false);
    setDraftSession(null);
    setCommandText("");
  }, [setDraftSession, setCommandText]);

  const onSave = useCallback(() => {
    dispatch({
      type: "space/exit-block-edit",
      source: "scene",
      action: "save",
    });
    if (blockName) {
      appendCommandFeed(t("summary.bclose_saved", { name: blockName }));
    }
    closeAndClearDraft();
  }, [dispatch, blockName, appendCommandFeed, t, closeAndClearDraft]);

  const onDiscard = useCallback(() => {
    dispatch({
      type: "space/exit-block-edit",
      source: "scene",
      action: "discard",
    });
    if (blockName) {
      appendCommandFeed(t("summary.bclose_discarded", { name: blockName }));
    }
    closeAndClearDraft();
  }, [dispatch, blockName, appendCommandFeed, t, closeAndClearDraft]);

  const onCancel = useCallback(() => {
    closeAndClearDraft();
  }, [closeAndClearDraft]);

  return {
    isOpen,
    props: {
      blockName,
      onSave,
      onDiscard,
      onCancel,
    },
  };
}
